import React, { useCallback, useEffect, useRef, useState } from 'react';
import { SEAT_ORDER } from '../utils/tableSeats';
import { useDoubleActivateWithHint } from '../utils/doubleActivate';

const HINT_TEXT = 'Double tap/click to watch/join table';
const HINT_FADE_MS = 5000;

function miniSeatEmojiRotation(seat) {
  const map = { north: 0, south: 180, east: 90, west: 270 };
  return map[seat] ?? 0;
}

function MiniSeatMarker({ seat, occupant }) {
  if (!occupant) {
    return <span className="table-mini-dot table-mini-dot--empty" />;
  }
  const isBot = occupant.type === 'site_bot' || occupant.type === 'external_bot';
  const isPlayer = occupant.type === 'player';
  if (!isBot && !isPlayer) {
    return <span className="table-mini-dot table-mini-dot--empty" />;
  }
  const emoji = isBot ? '💻' : '👤';
  const rotation = miniSeatEmojiRotation(seat);
  return (
    <span
      className={[
        'table-mini-dot',
        isBot ? 'table-mini-dot--bot' : 'table-mini-dot--player',
      ].join(' ')}
      title={occupant.username || occupant.botLabel || 'Seat'}
    >
      <span
        className="table-mini-emoji"
        style={{ transform: `rotate(${rotation}deg)` }}
        aria-hidden
      >
        {emoji}
      </span>
    </span>
  );
}

function MiniSeatButton({ seat, occupant, onSitAtTable, tableId, onShowHint }) {
  const canTake = !occupant
    || occupant.type === 'site_bot'
    || occupant.type === 'external_bot';

  const handleSit = useDoubleActivateWithHint(
    (e) => {
      e.stopPropagation();
      if (canTake) onSitAtTable(tableId, seat);
    },
    { onHint: onShowHint },
  );

  return (
    <button
      type="button"
      className={`table-mini-seat table-mini-seat--${seat}`}
      title={
        canTake
          ? `Double-click to sit at ${seat}`
          : `${occupant.username || 'Taken'}`
      }
      onDoubleClick={handleSit}
      onClick={handleSit}
    >
      <MiniSeatMarker seat={seat} occupant={occupant} />
    </button>
  );
}

function TableMiniFelt({ table, onWatchTable, onSitAtTable }) {
  const [showHint, setShowHint] = useState(false);
  const hintTimerRef = useRef(null);
  // Gin is 2-player (N/S) only once confirmed — during multi-game pick show all 4
  // so a table that last played gin can still be sat/hosted after reset.
  const ginLocked = table?.gameType === 'gin'
    && !table?.needsGamePick
    && table?.gameTypeConfirmed !== false;
  const seatSlots = ginLocked ? ['north', 'south'] : SEAT_ORDER;

  const revealHint = useCallback(() => {
    setShowHint(true);
    if (hintTimerRef.current) clearTimeout(hintTimerRef.current);
    hintTimerRef.current = setTimeout(() => {
      setShowHint(false);
      hintTimerRef.current = null;
    }, HINT_FADE_MS);
  }, []);

  useEffect(() => () => {
    if (hintTimerRef.current) clearTimeout(hintTimerRef.current);
  }, []);

  const handleWatch = useDoubleActivateWithHint(
    () => onWatchTable(table.id),
    { onHint: revealHint },
  );

  return (
    <div
      className={[
        'table-mini-felt',
        ginLocked ? 'table-mini-felt--gin' : '',
      ].filter(Boolean).join(' ')}
      role="presentation"
      title="Double-click table to watch"
      onDoubleClick={handleWatch}
      onClick={handleWatch}
    >
      {seatSlots.map((seat) => (
        <MiniSeatButton
          key={seat}
          seat={seat}
          occupant={table.seats?.[seat]}
          tableId={table.id}
          onSitAtTable={onSitAtTable}
          onShowHint={revealHint}
        />
      ))}
      <span className="table-mini-label" aria-hidden="true">{table.title}</span>
      {showHint && (
        <span className="table-mini-hint" role="status">
          {HINT_TEXT}
        </span>
      )}
    </div>
  );
}

function TableGrid({
  tables,
  canManageLobbies,
  onOpenTable,
  onCloseTable,
  onWatchTable,
  onSitAtTable,
}) {
  return (
    <div className="table-grid">
      <div className="table-grid-head">
        <h2>Tables</h2>
        {canManageLobbies && (
          <button type="button" className="table-grid-add" onClick={onOpenTable}>
            + Add table
          </button>
        )}
      </div>

      <p className="table-grid-hint">
        Double-click a seat to sit · double-click the table to watch
      </p>

      {tables.length === 0 ? (
        <p className="table-grid-empty">No tables yet.</p>
      ) : (
        <ul className="table-grid-list">
          {tables.map((t) => (
            <li key={t.id} className="table-grid-item">
              <div className="table-grid-card">
                <TableMiniFelt
                  table={t}
                  onWatchTable={onWatchTable}
                  onSitAtTable={onSitAtTable}
                />
                <div className="table-grid-meta-block">
                  <span className="table-grid-title">{t.title}</span>
                  <span className="table-grid-meta">
                    {t.humanSeatedCount ?? t.seatedCount}/4 players
                    {t.watcherCount > 0 && ` · ${t.watcherCount} watching`}
                    {t.status === 'playing' && ' · In progress'}
                  </span>
                  {t.hostUsername && (
                    <span className="table-grid-host">Host: {t.hostUsername}</span>
                  )}
                </div>
              </div>
              {canManageLobbies && (
                <button
                  type="button"
                  className="table-grid-remove"
                  title="Remove table"
                  onClick={() => onCloseTable(t.id)}
                >
                  ×
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default TableGrid;